export const technicalWritingCourse = {
  id: 'technical-writing',
  title: 'Technical Writing',
  description: 'Comprehensive course in technical communication, documentation standards, and professional writing for technology fields including software documentation, API guides, and technical proposals.',
  credits: 2,
  duration: '15 weeks',
  level: 'Intermediate',
  category: 'Coding',
  learningObjectives: [
    'Master technical writing principles and best practices',
    'Create clear, concise software documentation and user guides',
    'Develop skills in API documentation and technical specifications',
    'Learn to write effective technical proposals and reports',
    'Understand audience analysis and information architecture',
    'Apply version control and collaborative writing techniques'
  ],
  prerequisites: ['English Composition', 'Introduction to Programming'],
  skills: [
    'Technical Documentation',
    'API Documentation',
    'User Guide Writing',
    'Technical Proposals',
    'Information Architecture',
    'Content Strategy',
    'Version Control for Documentation',
    'Collaborative Writing',
    'Technical Editing',
    'Documentation Tools',
    'Visual Communication',
    'Professional Communication'
  ],
  marketDemand: 'Very High',
  averageSalary: '$60,000 - $110,000',
  careerPaths: [
    'Technical Writer',
    'Documentation Engineer',
    'Content Strategist',
    'Developer Relations Engineer',
    'Product Marketing Manager',
    'UX Writer',
    'Information Architect',
    'Training Content Developer',
    'Grant Writer',
    'Science Communicator',
    'Developer Advocate',
    'Technical Communications Manager'
  ],
  industryApplications: [
    'Software Documentation',
    'API and SDK Documentation',
    'User Manual Creation',
    'Technical Proposals',
    'Grant Writing',
    'Training Materials',
    'Marketing Content',
    'Compliance Documentation',
    'Research Publications',
    'Product Specifications',
    'Help Systems',
    'Knowledge Base Development'
  ],
  modules: [
    {
      id: 'technical-writing-fundamentals',
      title: 'Technical Writing Fundamentals',
      duration: '3 weeks',
      learningObjectives: [
        'Understand principles of clear technical communication',
        'Learn audience analysis and document planning',
        'Master technical writing style and tone',
        'Develop editing and revision skills'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Principles of Technical Communication',
          content: `# Technical Writing Fundamentals

## Core Principles

### Clarity
- Use simple, direct language
- Avoid jargon and unnecessary complexity
- Define technical terms when first introduced
- Structure information logically

### Conciseness
- Eliminate redundant words and phrases
- Use active voice when possible
- Choose precise verbs and nouns
- Remove unnecessary qualifiers

### Accuracy
- Verify all technical information
- Use consistent terminology
- Include relevant details
- Fact-check data and statistics

### Accessibility
- Write for your target audience
- Use inclusive language
- Consider different experience levels
- Provide multiple learning paths

## Audience Analysis

### Identify Your Readers
- **Primary Audience**: Main users of the document
- **Secondary Audience**: Others who may read it
- **Tertiary Audience**: Stakeholders and decision-makers

### Consider Reader Characteristics
- Technical expertise level
- Professional role and responsibilities
- Available time and attention span
- Goals and motivations for reading
- Cultural and linguistic background

### Document Planning Framework
1. **Purpose**: Why are you writing this?
2. **Audience**: Who will read this?
3. **Scope**: What will you cover?
4. **Format**: How will you present it?
5. **Timeline**: When is it needed?

## Technical Writing Style

### Voice and Tone
- **Professional**: Formal but not stiff
- **Helpful**: Guide readers to success
- **Confident**: Present information authoritatively
- **Respectful**: Acknowledge reader expertise

### Language Guidelines
- Use second person (you) for instructions
- Use active voice for clarity
- Choose specific, concrete words
- Avoid contractions in formal documents

### Common Style Issues
❌ **Avoid**: "You might want to consider perhaps trying to..."
✅ **Better**: "To complete this task, follow these steps:"

❌ **Avoid**: "The system will be configured by the administrator."
✅ **Better**: "The administrator configures the system."

## Information Architecture

### Document Structure
- **Introduction**: Purpose, scope, audience
- **Body**: Main content organized logically
- **Conclusion**: Summary, next steps
- **Appendices**: Supporting information

### Organizational Patterns
- **Chronological**: Step-by-step processes
- **Spatial**: Physical layouts or locations
- **Categorical**: Grouping by type or function
- **Hierarchical**: From general to specific
- **Problem-Solution**: Issue identification and resolution

## Editing and Revision Process

### Three-Pass Editing
1. **Content Edit**: Structure, completeness, accuracy
2. **Copy Edit**: Grammar, style, clarity
3. **Proofreading**: Spelling, punctuation, formatting

### Revision Checklist
- [ ] Is the purpose clear?
- [ ] Is information accurate and complete?
- [ ] Is the structure logical?
- [ ] Is the language appropriate for the audience?
- [ ] Are instructions testable and accurate?
- [ ] Are examples relevant and helpful?`
        },
        {
          type: 'interactive_exercise',
          title: 'Technical Writing Analysis Tool',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a technical writing analysis tool that evaluates document quality:

1. Analyze readability using metrics like Flesch-Kincaid score
2. Check for technical writing best practices
3. Identify passive voice and suggest active alternatives
4. Evaluate document structure and organization
5. Generate improvement recommendations

Your tool should process text and provide:
- Readability scores and grade level
- Passive voice detection and suggestions
- Jargon and complexity analysis
- Structure assessment
- Overall writing quality score

Test with a sample technical paragraph and provide detailed feedback.`,
            initialCode: `class TechnicalWritingAnalyzer {
  constructor() {
    this.passiveIndicators = ['was', 'were', 'been', 'being', 'be', 'is', 'are', 'am'];
    this.jargonTerms = ['utilize', 'facilitate', 'implement', 'leverage', 'optimize'];
  }
  
  // Calculate Flesch-Kincaid readability score
  calculateReadability(text) {
    const sentences = this.countSentences(text);
    const words = this.countWords(text);
    const syllables = this.countSyllables(text);
    
    // Flesch-Kincaid Grade Level formula
    const gradeLevel = 0.39 * (words / sentences) + 11.8 * (syllables / words) - 15.59;
    
    // Flesch Reading Ease formula
    const readingEase = 206.835 - 1.015 * (words / sentences) - 84.6 * (syllables / words);
    
    return {
      gradeLevel: Math.max(0, gradeLevel).toFixed(1),
      readingEase: Math.max(0, Math.min(100, readingEase)).toFixed(1),
      interpretation: this.interpretReadability(readingEase)
    };
  }
  
  // Detect passive voice usage
  detectPassiveVoice(text) {
    // Find instances of passive voice patterns
    // Return: { count: number, suggestions: array, percentage: number }
  }
  
  // Analyze document structure
  analyzeStructure(text) {
    // Check for headings, lists, paragraphs
    // Evaluate logical flow and organization
    // Return: { score: number, issues: array, suggestions: array }
  }
  
  // Check for jargon and complexity
  analyzeComplexity(text) {
    // Identify complex words, jargon, long sentences
    // Return: { jargonCount: number, avgSentenceLength: number, suggestions: array }
  }
  
  // Generate comprehensive analysis
  analyzeDocument(text) {
    // Combine all analysis methods
    // Return complete writing assessment
  }
  
  // Helper methods
  countSentences(text) {
    return (text.match(/[.!?]+/g) || []).length;
  }
  
  countWords(text) {
    return text.trim().split(/\\s+/).filter(word => word.length > 0).length;
  }
  
  countSyllables(text) {
    // Simplified syllable counting
    const words = text.toLowerCase().match(/\\b[a-z']+\\b/g) || [];
    return words.reduce((total, word) => {
      let syllables = word.match(/[aeiouy]+/g) || [];
      if (word.endsWith('e')) syllables.pop();
      return total + Math.max(1, syllables.length);
    }, 0);
  }
  
  interpretReadability(score) {
    if (score >= 90) return 'Very Easy';
    if (score >= 80) return 'Easy';
    if (score >= 70) return 'Fairly Easy';
    if (score >= 60) return 'Standard';
    if (score >= 50) return 'Fairly Difficult';
    if (score >= 30) return 'Difficult';
    return 'Very Difficult';
  }
}

// Test implementation
const analyzer = new TechnicalWritingAnalyzer();

const sampleText = \`
The implementation of the algorithm was completed by the development team. 
The system will be configured to utilize advanced optimization techniques. 
This will facilitate improved performance and will leverage existing infrastructure. 
The documentation should be reviewed to ensure compliance with established standards.
\`;

console.log("Technical Writing Analysis:");
const analysis = analyzer.analyzeDocument(sampleText);
console.log("Readability:", analysis.readability);
console.log("Passive Voice:", analysis.passiveVoice);
console.log("Overall Score:", analysis.overallScore + "/100");`,
            expectedOutput: `Technical Writing Analysis:
Readability: { gradeLevel: '12.3', readingEase: '45.2', interpretation: 'Fairly Difficult' }
Passive Voice: { count: 4, percentage: '25%', suggestions: ['Change "was completed by" to active voice', 'Replace "will be configured" with active construction'] }
Overall Score: 65/100`,
            hints: [
              'Flesch-Kincaid Grade = 0.39 * (words/sentences) + 11.8 * (syllables/words) - 15.59',
              'Look for passive voice patterns: "was/were + past participle"',
              'Count syllables by finding vowel clusters in words',
              'Identify jargon by checking against common business/tech buzzwords',
              'Grade documents on clarity, conciseness, and structure',
              'Provide specific suggestions for improvement'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Writing Analysis Project',
          title: 'Technical Document Review and Revision',
          points: 75,
          dueWeek: 3
        }
      ]
    },
    {
      id: 'software-documentation',
      title: 'Software Documentation and API Guides',
      duration: '4 weeks',
      learningObjectives: [
        'Create comprehensive software documentation',
        'Write clear API documentation and guides',
        'Develop user-friendly installation and setup instructions',
        'Master documentation tools and markup languages'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Software Documentation Best Practices',
          content: `# Software Documentation

## Types of Software Documentation

### User Documentation
- **Installation Guides**: Setup and configuration
- **User Manuals**: How to use the software
- **Tutorials**: Step-by-step learning paths
- **FAQs**: Common questions and answers
- **Troubleshooting Guides**: Problem resolution

### Developer Documentation
- **API Documentation**: Interface specifications
- **Code Comments**: Inline explanations
- **Technical Specifications**: System architecture
- **Contributing Guides**: Development workflows
- **README Files**: Project overviews

### Administrative Documentation
- **Deployment Guides**: Production setup
- **Security Documentation**: Safety protocols
- **Backup Procedures**: Data protection
- **Monitoring Guides**: System health checks

## API Documentation Structure

### Essential Components
1. **Overview**: What the API does
2. **Authentication**: How to authenticate
3. **Endpoints**: Available operations
4. **Parameters**: Required and optional inputs
5. **Response Format**: Expected outputs
6. **Error Codes**: Problem identification
7. **Examples**: Working code samples
8. **SDKs**: Available libraries

### API Endpoint Documentation Template
\`\`\`markdown
## GET /api/users/{id}

Retrieves a specific user by their unique identifier.

### Parameters
- **id** (required): The unique user identifier
  - Type: integer
  - Example: 12345

### Headers
- **Authorization**: Bearer token required
- **Content-Type**: application/json

### Response
\`\`\`json
{
  "id": 12345,
  "name": "John Doe",
  "email": "john.doe@example.com",
  "created_at": "2023-01-15T10:30:00Z"
}
\`\`\`

### Error Responses
- **404**: User not found
- **401**: Authentication required
- **403**: Access denied
\`\`\`

## Documentation Tools and Formats

### Markdown
- Lightweight markup language
- Easy to read and write
- Widely supported
- Version control friendly

### Documentation Generators
- **Swagger/OpenAPI**: API documentation
- **JSDoc**: JavaScript documentation
- **Sphinx**: Python documentation
- **GitBook**: Comprehensive guides
- **Docusaurus**: Static site generators

### Living Documentation
- Keep documentation close to code
- Automate updates when possible
- Use examples that actually work
- Regular review and updates

## Code Examples Best Practices

### Complete Examples
\`\`\`javascript
// ❌ Incomplete example
fetch('/api/users')

// ✅ Complete example
fetch('/api/users', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-token-here',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));
\`\`\`

### Multiple Language Examples
Provide examples in different programming languages when possible:

**JavaScript:**
\`\`\`javascript
const response = await fetch('/api/users/123');
const user = await response.json();
\`\`\`

**Python:**
\`\`\`python
import requests
response = requests.get('/api/users/123')
user = response.json()
\`\`\`

**cURL:**
\`\`\`bash
curl -X GET "/api/users/123" \\
  -H "Authorization: Bearer your-token"
\`\`\`

## Installation and Setup Documentation

### Prerequisites Section
- System requirements
- Dependencies
- Required permissions
- Environmental considerations

### Step-by-Step Instructions
1. **Clear numbering**: Use numbered lists
2. **Expected outcomes**: What users should see
3. **Verification steps**: How to confirm success
4. **Troubleshooting**: Common issues and solutions

### Example Installation Guide
\`\`\`markdown
# Installation Guide

## Prerequisites
- Node.js 16.0 or higher
- npm 7.0 or higher
- Git

## Installation Steps

1. Clone the repository:
   \`\`\`bash
   git clone https://github.com/example/project.git
   \`\`\`

2. Navigate to the project directory:
   \`\`\`bash
   cd project
   \`\`\`

3. Install dependencies:
   \`\`\`bash
   npm install
   \`\`\`

4. Create environment file:
   \`\`\`bash
   cp .env.example .env
   \`\`\`

5. Start the development server:
   \`\`\`bash
   npm run dev
   \`\`\`

## Verification
Open your browser to http://localhost:3000. You should see the welcome page.

## Troubleshooting
- **Port already in use**: Try \`npm run dev -- --port 3001\`
- **Permission errors**: Check file permissions and user access
\`\`\``
        },
        {
          type: 'interactive_exercise',
          title: 'API Documentation Generator',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create an API documentation generator that:

1. Parses API endpoint definitions
2. Generates formatted documentation
3. Creates interactive examples
4. Validates documentation completeness
5. Exports to multiple formats (Markdown, HTML, OpenAPI)

Your generator should handle:
- REST API endpoints with methods, parameters, responses
- Authentication requirements
- Error code documentation
- Code examples in multiple languages
- Interactive testing capabilities

Build a tool that takes API specifications and produces comprehensive documentation.`,
            initialCode: `class APIDocumentationGenerator {
  constructor() {
    this.endpoints = [];
    this.globalConfig = {};
  }
  
  // Add API endpoint specification
  addEndpoint(spec) {
    // Add endpoint with method, path, parameters, responses
    // Validate required fields are present
    // spec: { method, path, summary, parameters, responses, examples }
  }
  
  // Set global configuration
  setGlobalConfig(config) {
    // Set API base URL, authentication, version info
    // config: { baseUrl, version, auth, title, description }
  }
  
  // Generate Markdown documentation
  generateMarkdown() {
    // Create comprehensive Markdown documentation
    // Include table of contents, endpoint details, examples
    // Return: formatted markdown string
  }
  
  // Generate OpenAPI specification
  generateOpenAPI() {
    // Convert to OpenAPI 3.0 format
    // Include all endpoints, schemas, authentication
    // Return: OpenAPI JSON object
  }
  
  // Create interactive examples
  generateExamples(endpoint, language = 'javascript') {
    // Generate working code examples for endpoints
    // Support: javascript, python, curl, php
    // Return: formatted code string
  }
  
  // Validate documentation completeness
  validateDocumentation() {
    // Check for missing required fields
    // Verify example consistency
    // Return: { valid: boolean, issues: array, score: number }
  }
  
  // Generate complete documentation package
  generateDocs(format = 'markdown') {
    // Create full documentation in specified format
    // Include validation and quality checks
  }
}

// Example API specification
const apiSpec = {
  method: 'GET',
  path: '/api/users/{id}',
  summary: 'Get user by ID',
  parameters: [
    { name: 'id', type: 'integer', required: true, description: 'User ID' }
  ],
  responses: {
    200: {
      description: 'User found',
      schema: {
        id: 'integer',
        name: 'string',
        email: 'string'
      }
    },
    404: { description: 'User not found' }
  },
  examples: {
    request: '/api/users/123',
    response: { id: 123, name: 'John Doe', email: 'john@example.com' }
  }
};

// Test implementation
const docGen = new APIDocumentationGenerator();
docGen.setGlobalConfig({
  title: 'User Management API',
  version: '1.0.0',
  baseUrl: 'https://api.example.com'
});

docGen.addEndpoint(apiSpec);
const markdown = docGen.generateMarkdown();
const validation = docGen.validateDocumentation();

console.log("Generated documentation length:", markdown.length);
console.log("Documentation validation:", validation);`,
            expectedOutput: `Generated documentation length: 1247
Documentation validation: { valid: true, issues: [], score: 95 }`,
            hints: [
              'Structure documentation with clear sections: overview, authentication, endpoints',
              'Include complete request/response examples with realistic data',
              'Generate code samples that actually work with the API',
              'Validate that all required fields are documented',
              'Use consistent formatting and clear parameter descriptions',
              'Include error handling examples and common use cases'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'API Documentation Project',
          title: 'Complete Software Documentation Suite',
          points: 125,
          dueWeek: 7
        }
      ]
    },
    {
      id: 'technical-proposals-reports',
      title: 'Technical Proposals and Reports',
      duration: '4 weeks',
      learningObjectives: [
        'Write persuasive technical proposals',
        'Create comprehensive technical reports',
        'Develop project documentation and specifications',
        'Master visual communication in technical documents'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Technical Proposal Writing',
          content: `# Technical Proposals and Reports

## Technical Proposal Structure

### Executive Summary
- **Problem Statement**: Clear issue identification
- **Proposed Solution**: High-level approach
- **Benefits**: Value proposition
- **Investment Required**: Resources needed
- **Timeline**: Project duration

### Technical Approach
- **Methodology**: How you'll solve the problem
- **Architecture**: System design overview
- **Implementation Plan**: Step-by-step approach
- **Risk Assessment**: Potential challenges
- **Alternatives Considered**: Other options evaluated

### Project Management
- **Timeline**: Detailed schedule with milestones
- **Resources**: Personnel, equipment, budget
- **Deliverables**: What will be produced
- **Success Metrics**: How to measure success
- **Dependencies**: External requirements

### Appendices
- **Technical Specifications**: Detailed requirements
- **Team Qualifications**: Expertise demonstration
- **References**: Supporting documentation
- **Budget Breakdown**: Cost details

## Writing Persuasive Technical Content

### Appeal to Logic
- Use data and evidence
- Provide clear reasoning
- Show cause-and-effect relationships
- Include relevant case studies

### Address Stakeholder Concerns
- **Technical Team**: Implementation feasibility
- **Management**: ROI and strategic value
- **Users**: Usability and benefits
- **Finance**: Cost justification

### Risk Mitigation Strategies
- Identify potential problems
- Propose solutions for each risk
- Show contingency planning
- Demonstrate preparedness

## Technical Report Types

### Progress Reports
- **Current Status**: What's been accomplished
- **Challenges**: Problems encountered
- **Solutions**: How issues were resolved
- **Next Steps**: Upcoming activities
- **Timeline Updates**: Schedule adjustments

### Research Reports
- **Background**: Context and motivation
- **Methodology**: Research approach
- **Findings**: Results and data
- **Analysis**: Interpretation of results
- **Recommendations**: Suggested actions

### Incident Reports
- **Incident Description**: What happened
- **Impact Assessment**: Effects on systems/users
- **Root Cause Analysis**: Why it happened
- **Resolution Steps**: How it was fixed
- **Prevention Measures**: Avoiding recurrence

## Visual Communication

### Effective Diagrams
- **System Architecture**: High-level component view
- **Process Flows**: Step-by-step procedures
- **Network Diagrams**: Infrastructure layouts
- **Timeline Charts**: Project schedules
- **Data Visualizations**: Metrics and trends

### Design Principles
- **Clarity**: Easy to understand at a glance
- **Consistency**: Uniform style and notation
- **Relevance**: Supports the written content
- **Accessibility**: Clear for all audiences
- **Professional**: Polished appearance

### Tools for Technical Visuals
- **Diagramming**: Lucidchart, Draw.io, Visio
- **Data Visualization**: Tableau, Power BI, D3.js
- **Mockups**: Figma, Sketch, Adobe XD
- **Screenshots**: Annotated with explanations
- **Code Diagrams**: UML, flowcharts, ERDs

## Specification Documents

### Requirements Documents
- **Functional Requirements**: What the system must do
- **Non-functional Requirements**: Performance, security, usability
- **User Stories**: Feature descriptions from user perspective
- **Acceptance Criteria**: Definition of done
- **Constraints**: Limitations and boundaries

### Technical Specifications
- **Architecture Overview**: System design
- **API Specifications**: Interface definitions
- **Database Schema**: Data structure
- **Security Requirements**: Protection measures
- **Performance Targets**: Speed and scalability goals

### Example Specification Section
\`\`\`markdown
## User Authentication System

### Functional Requirements
- FR-1: Users must be able to register with email and password
- FR-2: System must support password reset via email
- FR-3: Users must be able to log in using email/password or OAuth
- FR-4: System must track login attempts and lock accounts after 5 failed attempts

### Non-Functional Requirements
- NFR-1: Password reset emails must be sent within 30 seconds
- NFR-2: Login process must complete within 2 seconds
- NFR-3: System must support 10,000 concurrent users
- NFR-4: All passwords must be hashed using bcrypt with minimum 12 rounds

### Security Requirements
- SEC-1: All authentication data must be transmitted over HTTPS
- SEC-2: Sessions must expire after 24 hours of inactivity
- SEC-3: Failed login attempts must be logged and monitored
- SEC-4: Password complexity must meet OWASP guidelines
\`\`\`

## Collaborative Writing Process

### Document Review Cycles
1. **Technical Review**: Subject matter experts
2. **Editorial Review**: Writing and clarity
3. **Stakeholder Review**: Business alignment
4. **Final Review**: Legal and compliance

### Version Control for Documents
- Use meaningful version numbers
- Track changes and comments
- Maintain change logs
- Archive previous versions
- Coordinate simultaneous edits`
        },
        {
          type: 'interactive_exercise',
          title: 'Technical Proposal Generator',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Build a technical proposal generation tool that:

1. Guides users through proposal structure
2. Generates professional document templates
3. Includes section prompts and best practices
4. Creates visual elements like timelines and budgets
5. Exports to multiple formats (PDF, Word, HTML)

Your tool should handle:
- Executive summary generation
- Technical approach documentation
- Project timeline creation
- Budget calculation and presentation
- Risk assessment frameworks

Create a tool that helps technical professionals write compelling proposals.`,
            initialCode: `class TechnicalProposalGenerator {
  constructor() {
    this.proposal = {
      executiveSummary: {},
      technicalApproach: {},
      projectManagement: {},
      budget: {},
      appendices: []
    };
  }
  
  // Generate executive summary
  generateExecutiveSummary(problemStatement, solution, benefits, timeline) {
    // Create compelling executive summary
    // Structure: problem → solution → benefits → ask
    // Return: formatted summary object
  }
  
  // Create technical approach section
  developTechnicalApproach(methodology, architecture, implementation, risks) {
    // Document technical solution details
    // Include diagrams, specifications, alternatives
    // Return: structured technical content
  }
  
  // Build project timeline
  createProjectTimeline(phases, milestones, dependencies) {
    // Generate Gantt chart data and timeline visualization
    // Calculate critical path and resource allocation
    // Return: { timeline: array, gantt: object, milestones: array }
  }
  
  // Calculate project budget
  calculateBudget(resources, equipment, overhead, contingency = 0.1) {
    // Break down costs by category
    // Apply contingency percentage
    // Return: detailed budget breakdown
  }
  
  // Generate risk assessment
  assessRisks(risks) {
    // Evaluate probability and impact
    // Suggest mitigation strategies
    // Return: risk matrix and mitigation plans
  }
  
  // Export proposal to different formats
  exportProposal(format = 'markdown') {
    // Generate complete proposal document
    // Support: markdown, html, pdf-ready
    // Include all sections and formatting
  }
  
  // Validate proposal completeness
  validateProposal() {
    // Check required sections and content quality
    // Return: { complete: boolean, missing: array, score: number }
  }
}

// Example usage data
const projectData = {
  problem: "Legacy system causing 40% performance degradation",
  solution: "Microservices architecture migration",
  benefits: ["50% performance improvement", "90% reduced downtime", "Modern scalability"],
  phases: [
    { name: "Analysis", duration: 4, dependencies: [] },
    { name: "Design", duration: 6, dependencies: ["Analysis"] },
    { name: "Implementation", duration: 12, dependencies: ["Design"] },
    { name: "Testing", duration: 4, dependencies: ["Implementation"] }
  ],
  budget: {
    personnel: 240000,
    equipment: 50000,
    software: 30000,
    overhead: 0.15
  }
};

// Test implementation
const generator = new TechnicalProposalGenerator();

const summary = generator.generateExecutiveSummary(
  projectData.problem,
  projectData.solution,
  projectData.benefits,
  "6 months"
);

const timeline = generator.createProjectTimeline(projectData.phases);
const budget = generator.calculateBudget(projectData.budget);
const validation = generator.validateProposal();

console.log("Executive Summary:", summary.length, "characters");
console.log("Project Duration:", timeline.totalWeeks, "weeks");
console.log("Total Budget:", budget.total);
console.log("Proposal Completeness:", validation.score + "%");`,
            expectedOutput: `Executive Summary: 847 characters
Project Duration: 26 weeks
Total Budget: $368000
Proposal Completeness: 85%`,
            hints: [
              'Executive summary should be compelling and concise (1-2 pages max)',
              'Include clear problem statement, proposed solution, and expected benefits',
              'Calculate timeline by considering dependencies between phases',
              'Add overhead and contingency to base costs for realistic budgets',
              'Validate that all required sections have meaningful content',
              'Structure content logically with clear headings and flow'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Technical Proposal Project',
          title: 'Complete Technical Proposal Development',
          points: 150,
          dueWeek: 11
        }
      ]
    },
    {
      id: 'collaborative-documentation',
      title: 'Collaborative Writing and Version Control',
      duration: '4 weeks',
      learningObjectives: [
        'Master collaborative writing workflows',
        'Apply version control to documentation projects',
        'Develop content management strategies',
        'Create sustainable documentation systems'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Documentation Workflows and Management',
          content: `# Collaborative Documentation

## Version Control for Documentation

### Git-Based Workflows
- **Docs as Code**: Treat documentation like software
- **Branch Management**: Feature branches for changes
- **Pull Requests**: Review process for contributions
- **Merge Conflicts**: Resolving competing edits
- **Release Management**: Documentation versioning

### Documentation Repositories
\`\`\`bash
# Typical docs repository structure
docs/
├── README.md
├── CONTRIBUTING.md
├── api/
│   ├── authentication.md
│   ├── endpoints.md
│   └── examples/
├── guides/
│   ├── getting-started.md
│   ├── installation.md
│   └── troubleshooting.md
├── reference/
│   ├── cli.md
│   └── configuration.md
└── assets/
    ├── images/
    └── diagrams/
\`\`\`

### Collaborative Writing Tools
- **GitHub/GitLab**: Code repository with docs
- **GitBook**: Collaborative writing platform
- **Notion**: Team knowledge management
- **Confluence**: Enterprise wiki system
- **Google Docs**: Real-time collaboration

## Content Management Strategy

### Information Architecture
- **User Journey Mapping**: How readers navigate
- **Content Hierarchy**: Logical organization
- **Cross-References**: Linking related content
- **Search Optimization**: Findable information
- **Maintenance Workflows**: Keeping content current

### Style Guides and Standards
- **Writing Style**: Voice, tone, terminology
- **Formatting**: Headings, lists, emphasis
- **Code Examples**: Consistent formatting
- **Image Guidelines**: Size, quality, alt text
- **Review Processes**: Quality assurance

### Example Style Guide
\`\`\`markdown
# Documentation Style Guide

## Voice and Tone
- **Professional**: Authoritative but approachable
- **Helpful**: Focus on user success
- **Clear**: Avoid jargon and complexity
- **Consistent**: Use standard terminology

## Formatting Standards
- Use sentence case for headings
- Bold for UI elements: Click **Save**
- Code formatting for: \`variables\`, \`functions()\`, \`file-names\`
- Numbered lists for procedures
- Bullet lists for features or options

## Code Examples
- Include complete, working examples
- Add comments for complex logic
- Test all code before publishing
- Provide multiple language examples when possible

## Images and Diagrams
- Maximum width: 800px
- Include descriptive alt text
- Use consistent styling and colors
- Optimize file sizes for web
\`\`\`

## Sustainable Documentation Systems

### Automation and Integration
- **Continuous Integration**: Automated testing
- **Link Checking**: Broken link detection
- **Content Validation**: Spelling and grammar
- **Deployment**: Automatic publishing
- **Metrics Collection**: Usage analytics

### Content Lifecycle Management
1. **Planning**: Identify documentation needs
2. **Creation**: Write initial content
3. **Review**: Quality assurance process
4. **Publication**: Make content available
5. **Maintenance**: Regular updates and improvements
6. **Archival**: Remove outdated content

### Community Contributions
- **Contribution Guidelines**: How to help
- **Issue Templates**: Bug reports and requests
- **Review Process**: Maintaining quality
- **Recognition**: Acknowledging contributors
- **Onboarding**: Training new contributors

## Documentation Metrics and Analytics

### Key Performance Indicators
- **Page Views**: Most popular content
- **Search Queries**: What users look for
- **Bounce Rate**: Content effectiveness
- **User Feedback**: Satisfaction scores
- **Contribution Rate**: Community engagement

### Continuous Improvement
- Regular content audits
- User feedback integration
- Performance optimization
- Accessibility improvements
- Mobile responsiveness

## Team Coordination

### Roles and Responsibilities
- **Technical Writers**: Content creation and editing
- **Subject Matter Experts**: Technical accuracy
- **Developers**: Code examples and integration
- **Designers**: Visual elements and UX
- **Product Managers**: Content strategy and priorities

### Communication Workflows
- Regular review meetings
- Shared content calendars
- Status tracking systems
- Feedback collection processes
- Cross-team collaboration protocols`
        },
        {
          type: 'interactive_exercise',
          title: 'Documentation Management System',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a documentation management system that handles:

1. Content versioning and collaboration workflows
2. Style guide enforcement and validation
3. Link checking and content validation
4. Analytics and performance tracking
5. Community contribution management

Your system should provide:
- Version control integration for documentation
- Automated quality checks and validation
- Content analytics and improvement suggestions
- Collaborative review and approval processes
- Style guide compliance monitoring

Build a comprehensive tool for managing technical documentation projects.`,
            initialCode: `class DocumentationManager {
  constructor() {
    this.documents = new Map();
    this.styleGuide = {};
    this.analytics = {};
    this.collaborators = [];
  }
  
  // Add document to management system
  addDocument(id, content, metadata) {
    // Track document with version history
    // metadata: { author, created, lastModified, reviewers, status }
  }
  
  // Validate document against style guide
  validateDocument(id) {
    // Check style guide compliance
    // Return: { valid: boolean, violations: array, score: number }
  }
  
  // Check all links in document
  checkLinks(id) {
    // Validate internal and external links
    // Return: { broken: array, redirects: array, status: string }
  }
  
  // Track document analytics
  trackAnalytics(id, event, metadata = {}) {
    // Record page views, searches, feedback
    // event: 'view', 'search', 'feedback', 'edit'
  }
  
  // Generate analytics report
  generateAnalyticsReport(timeframe = '30d') {
    // Analyze document performance and usage
    // Return: { popular: array, issues: array, suggestions: array }
  }
  
  // Manage collaborative reviews
  createReviewRequest(documentId, reviewers, deadline) {
    // Set up document review process
    // Track reviewer feedback and approvals
  }
  
  // Export documentation site
  exportSite(format = 'static') {
    // Generate complete documentation website
    // Support: static, gitbook, confluence
  }
}

// Style guide configuration
const styleGuide = {
  maxHeadingLength: 60,
  preferredVoice: 'active',
  requiredSections: ['overview', 'examples', 'parameters'],
  imageMaxSize: 800,
  linkValidation: true,
  grammarCheck: true
};

// Sample document content
const sampleDoc = {
  id: 'api-authentication',
  content: \`# API Authentication Guide
  
This guide explains how to authenticate with our REST API.

## Overview
The API uses Bearer token authentication for secure access.

## Getting Started
1. Register for an account
2. Generate an API key
3. Include token in Authorization header

## Examples
\\\`\\\`\\\`javascript
fetch('/api/data', {
  headers: {
    'Authorization': 'Bearer your-token-here'
  }
});
\\\`\\\`\\\`
\`,
  metadata: {
    author: 'tech-writer',
    created: new Date('2023-01-15'),
    status: 'published',
    reviewers: ['senior-dev', 'product-manager']
  }
};

// Test implementation
const docManager = new DocumentationManager();
docManager.addDocument(sampleDoc.id, sampleDoc.content, sampleDoc.metadata);

const validation = docManager.validateDocument(sampleDoc.id);
const linkCheck = docManager.checkLinks(sampleDoc.id);
const analytics = docManager.generateAnalyticsReport();

console.log("Document validation:", validation.score + "/100");
console.log("Link status:", linkCheck.status);
console.log("Top performing docs:", analytics.popular.slice(0, 3));`,
            expectedOutput: `Document validation: 92/100
Link status: all_valid
Top performing docs: ['api-authentication', 'getting-started', 'troubleshooting']`,
            hints: [
              'Store documents with version history and metadata tracking',
              'Validate style guide rules: heading length, required sections, formatting',
              'Check links by parsing markdown and validating URLs',
              'Track analytics events: page views, time on page, user feedback',
              'Score documents based on completeness, style compliance, and user engagement',
              'Integrate with Git workflow for version control and collaboration'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Documentation System Project',
          title: 'Collaborative Documentation Platform Implementation',
          points: 125,
          dueWeek: 15
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'midterm',
      title: 'Midterm Portfolio Review',
      type: 'portfolio',
      date: 'Week 8',
      duration: '1 week',
      points: 150,
      description: 'Comprehensive review of documentation portfolio including technical writing samples, style guide compliance, and peer review participation.'
    },
    {
      id: 'final',
      title: 'Capstone Documentation Project',
      type: 'project',
      date: 'Week 15',
      duration: '2 weeks',
      points: 200,
      description: 'Complete documentation suite for a technical project including user guides, API documentation, and technical proposals.'
    }
  ],
  resources: [
    {
      category: 'Writing Guides',
      items: [
        'The Elements of Style by Strunk and White',
        'Technical Writing for Dummies by Sheryl Lindsell-Roberts',
        'The Sense of Style by Steven Pinker',
        'Google Developer Documentation Style Guide'
      ]
    },
    {
      category: 'Documentation Tools',
      items: [
        'GitBook for collaborative writing',
        'Notion for team knowledge management',
        'Markdown editors (Typora, Mark Text)',
        'Diagram tools (Lucidchart, Draw.io)',
        'Grammar checkers (Grammarly, Hemingway Editor)'
      ]
    },
    {
      category: 'Reference Materials',
      items: [
        'API Documentation Best Practices',
        'Technical Writing Style Guides',
        'Markdown Quick Reference',
        'Documentation Site Examples',
        'Version Control for Writers'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Weekly Writing Assignments': '25%',
    'Documentation Projects': '35%',
    'Midterm Portfolio': '20%',
    'Final Capstone Project': '20%'
  },
  attendancePolicy: 'Regular attendance essential for peer review workshops and collaborative writing sessions.',
  latePolicy: 'Writing assignments penalized 10% per day late. Extensions available for major projects with advance notice.',
  accommodations: 'Accommodations available including extended time for writing assignments, alternative formats, and assistive technology.',
  academicIntegrity: 'Original writing required with proper citation of sources. Collaboration encouraged on peer reviews with individual accountability.',
  technologyRequirements: 'Computer with word processing software, internet access for research and collaboration tools.',
  softwareRequirements: [
    'Word processor (Microsoft Word, Google Docs)',
    'Markdown editor',
    'Collaboration tools (Google Workspace, GitHub)',
    'Reference management software',
    'Grammar and style checking tools'
  ],
  courseEvaluation: 'Student feedback collected through peer reviews, self-reflection essays, and industry relevance assessments.'
};